// frontend/src/pages/OptimizationHubPage.tsx

import React, {
  useMemo,
} from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {
  useQuery,
} from '@tanstack/react-query';

import {
  Link as RouterLink,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import PageHeader, { pageFrameSx } from '../modules/pricingJourney/components/PageHeader';
import { currentOrigin, withReturnTo } from '../modules/pricingJourney/navigation';
import EmptyState from '../modules/pricingJourney/components/EmptyState';
import HistoryToggleOffRoundedIcon from '@mui/icons-material/HistoryToggleOffRounded';
import { SkeletonTable } from '../modules/pricingJourney/components/Skeletons';

import ArrowForwardRoundedIcon
  from '@mui/icons-material/ArrowForwardRounded';

import ViewListRoundedIcon
  from '@mui/icons-material/ViewListRounded';

import ArrowOutwardRoundedIcon
  from '@mui/icons-material/ArrowOutwardRounded';

import QueryStatsRoundedIcon
  from '@mui/icons-material/QueryStatsRounded';

import OptimizationEntityNavigator
  from './OptimizationEntityNavigator';

import {
  getOptimizationHistory,
  type OptimizationLogEntry,
} from '../services/optimizationService';

import {
  getJobsList,
} from '../services/batchService';
import { pricingNumberFormat } from '../modules/pricingJourney/formatNumber';


const surface = {
  borderRadius: 3,
  border:
    '1px solid rgba(148,163,184,0.16)',
  bgcolor:
    'rgba(15,23,42,0.70)',
  backgroundImage: 'none',
  boxShadow: 'none',
};


const formatDate = (
  value: string | null | undefined,
): string => {
  if (!value) {
    return '—';
  }

  const parsed =
    new Date(value);

  if (
    !Number.isFinite(
      parsed.getTime(),
    )
  ) {
    return '—';
  }

  return new Intl.DateTimeFormat(
    'es-ES',
    {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    },
  ).format(parsed);
};


const formatPrice = (
  value: number | null | undefined,
): string => (
  value === null
  || value === undefined
  || !Number.isFinite(value)
    ? '—'
    : pricingNumberFormat({
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    ).format(value)
);


const strategyLabel = (
  row: OptimizationLogEntry,
): string => {
  const profile =
    row.strategy_profile
      ?.toLowerCase();

  const labels:
  Record<string, string> = {
    conservador: 'Conservadora',
    equilibrado: 'Equilibrada',
    agresivo: 'Agresiva',
    manual: 'Manual',
  };

  return (
    labels[profile ?? '']
    ?? row.strategy_profile
    ?? 'Sin estrategia'
  );
};


const batchStatusLabel = (
  status: string | null | undefined,
): string => {
  const labels:
  Record<string, string> = {
    PENDING: 'Pendiente',
    RUNNING: 'En proceso',
    COMPLETED: 'Completado',
    FAILED: 'Fallido',
    COMPLETED_WITH_ERRORS:
      'Completado con incidencias',
  };

  return (
    labels[status ?? '']
    ?? status
    ?? 'Sin ejecuciones'
  );
};


const OptimizationHubPage:
React.FC = () => {
  const navigate =
    useNavigate();
  const location = useLocation();

  const {
    data: history = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: [
      'optimizationHistory',
    ],
    queryFn:
      getOptimizationHistory,
    retry: 1,
  });

  const batchJobsQuery =
    useQuery({
      queryKey: [
        'optimizationBatchJobs',
      ],
      queryFn:
        getJobsList,
      retry: 1,
      staleTime: 30_000,
    });

  const sortedHistory =
    useMemo(
      () => (
        [...history]
          .sort(
            (left, right) =>
              new Date(
                right.created_at,
              ).getTime()
              - new Date(
                left.created_at,
              ).getTime(),
          )
      ),
      [history],
    );

  const recent =
    useMemo(
      () =>
        sortedHistory.slice(0, 10),
      [sortedHistory],
    );

  const successfulAvailable =
    useMemo(
      () =>
        history.filter(
          (row) => row.success,
        ).length,
      [history],
    );

  const latestDate =
    sortedHistory[0]?.created_at
    ?? null;

  const distinctCustomers =
    useMemo(
      () =>
        new Set(
          history.flatMap(
            (row) =>
              row.customer_id
                ? [row.customer_id]
                : [],
          ),
        ).size,
      [history],
    );

  const distinctProducts =
    useMemo(
      () =>
        new Set(
          history.flatMap(
            (row) =>
              row.product_id
                ? [row.product_id]
                : [],
          ),
        ).size,
      [history],
    );

  const batchJobs =
    batchJobsQuery.data
    ?? [];

  const latestBatchJob =
    batchJobs[0]
    ?? null;

  const activeBatchJobs =
    batchJobs.filter(
      (job) =>
        job.status === 'PENDING'
        || job.status === 'RUNNING',
    ).length;

  const latestBatchProgress =
    latestBatchJob?.total_rows
    && latestBatchJob.total_rows > 0
      ? `${
          pricingNumberFormat().format(
            latestBatchJob.processed_rows,
          )
        } / ${
          pricingNumberFormat().format(
            latestBatchJob.total_rows,
          )
        }`
      : latestBatchJob
        ? pricingNumberFormat().format(
            latestBatchJob.processed_rows,
          )
        : '—';

  const evaluationHref = (
    row: OptimizationLogEntry,
  ) => withReturnTo(
    `/optimization/evaluations/${encodeURIComponent(row.optimization_id ?? '')}`,
    `/optimization-center${location.search}`,
  );

  return (
    <Box
      sx={pageFrameSx}
    >
      <Stack spacing={2}>
        {/* PRC-032: cabecera común; «Volver» solo con origen (PRC-027). */}
        <PageHeader
          eyebrow="Optimización"
          title="Optimización"
          description="Abre un cliente o producto para analizar su contexto y evaluar decisiones de precio."
          back="origin"
          sx={{ mb: 0 }}
          actions={(
            // PRC-027: el evaluador antiguo, enlazado como «Evaluación rápida».
            <Button
              variant="outlined"
              onClick={() => navigate(withReturnTo('/optimization', currentOrigin(location)))}
              sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 800 }}
            >
              Evaluación rápida
            </Button>
          )}
        />

        <OptimizationEntityNavigator />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              xl:
                'minmax(0, 4fr) minmax(300px, 1fr)',
            },
            gap: 2,
            alignItems: 'stretch',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              ...surface,
              overflow: 'hidden',
              minWidth: 0,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack
              direction={{
                xs: 'column',
                sm: 'row',
              }}
              justifyContent="space-between"
              alignItems={{
                xs: 'flex-start',
                sm: 'center',
              }}
              spacing={1.5}
              sx={{
                px: 2.5,
                py: 1.8,
              }}
            >
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: '#38bdf8',
                    fontWeight: 850,
                    letterSpacing: 0.9,
                  }}
                >
                  Actividad
                </Typography>

                <Typography
                  variant="subtitle1"
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 900,
                    mt: 0.05,
                  }}
                >
                  Evaluaciones recientes
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: '#8298ae',
                    mt: 0.15,
                  }}
                >
                  Recomendaciones calculadas,
                  no precios aplicados.
                </Typography>
              </Box>

              <Button
                size="small"
                endIcon={
                  <ArrowForwardRoundedIcon
                    fontSize="small"
                  />
                }
                onClick={() =>
                  navigate(
                    withReturnTo('/optimization/history', currentOrigin(location)),
                  )
                }
                sx={{
                  color: '#7dd3fc',
                  fontWeight: 800,
                  textTransform: 'none',
                  flexShrink: 0,
                }}
              >
                Ver historial completo
              </Button>
            </Stack>

            <Divider
              sx={{
                borderColor:
                  'rgba(148,163,184,0.14)',
              }}
            />

            {/* PRC-031: filas en gris mientras carga. */}
            {isLoading && (
              <Box sx={{ p: 2 }}><SkeletonTable rows={5} /></Box>
            )}

            {isError && (
              <Box
                sx={{
                  p: 2.5,
                  flex: 1,
                }}
              >
                <Alert
                  severity="warning"
                  action={
                    <Button
                      color="inherit"
                      onClick={() =>
                        void refetch()
                      }
                    >
                      Reintentar
                    </Button>
                  }
                >
                  No se pudo recuperar la
                  actividad reciente.
                </Alert>
              </Box>
            )}

            {!isLoading
              && !isError
              && recent.length === 0 && (
              // PRC-032: estado vacío común.
              <Box sx={{ p: 2, flex: 1 }}>
                <EmptyState
                  icon={<HistoryToggleOffRoundedIcon />}
                  title="Aún no hay evaluaciones"
                  text="Busca un cliente o producto arriba y evalúa un precio; aparecerá aquí."
                  minHeight={180}
                />
              </Box>
            )}

            {!isLoading
              && !isError
              && recent.length > 0 && (
              <TableContainer
                sx={{
                  // PRC-032: sin barra vertical propia dentro de la página.
                  flex: 1,
                }}
              >
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    minWidth: 820,
                    tableLayout: 'fixed',
                    '& th': {
                      bgcolor: '#0c1829',
                      color: '#91a9bd',
                      borderColor:
                        '#283d50',
                      fontSize: 11,
                      fontWeight: 850,
                      py: 1.25,
                    },
                    '& td': {
                      borderColor:
                        '#26394e',
                      color: '#e2e8f0',
                      py: 1.25,
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          width: 105,
                        }}
                      >
                        FECHA
                      </TableCell>

                      <TableCell
                        sx={{
                          width: 205,
                        }}
                      >
                        CLIENTE
                      </TableCell>

                      <TableCell
                        sx={{
                          width: 150,
                        }}
                      >
                        PRODUCTO
                      </TableCell>

                      <TableCell
                        sx={{
                          width: 130,
                        }}
                      >
                        ESTRATEGIA
                      </TableCell>

                      <TableCell
                        align="right"
                        sx={{
                          width: 110,
                        }}
                      >
                        COSTE*
                      </TableCell>

                      <TableCell
                        align="right"
                        sx={{
                          width: 125,
                        }}
                      >
                        RECOMENDACIÓN*
                      </TableCell>

                      <TableCell
                        align="right"
                        sx={{
                          width: 115,
                        }}
                      >
                        MARGEN*
                      </TableCell>

                      <TableCell
                        sx={{
                          width: 115,
                        }}
                      >
                        ESTADO
                      </TableCell>

                      <TableCell
                        align="right"
                        sx={{
                          width: 82,
                        }}
                      >
                        FICHA
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {recent.map(
                      (row) => (
                        <TableRow
                          key={row.id}
                          hover
                          sx={{
                            '&:hover': {
                              bgcolor:
                                '#192c40',
                            },
                          }}
                        >
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 750,
                                whiteSpace:
                                  'nowrap',
                              }}
                            >
                              {formatDate(
                                row.created_at,
                              )}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 800,
                                color: '#d7ecff',
                                overflow: 'hidden',
                                textOverflow:
                                  'ellipsis',
                                whiteSpace:
                                  'nowrap',
                              }}
                              title={
                                row.customer_name
                                || row.customer_id
                                || 'Cliente nuevo'
                              }
                            >
                              {row.customer_name
                                || row.customer_id
                                || 'Cliente nuevo'}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: '#bdd1df',
                                overflow: 'hidden',
                                textOverflow:
                                  'ellipsis',
                                whiteSpace:
                                  'nowrap',
                              }}
                              title={
                                row.product_id
                                || 'No identificado'
                              }
                            >
                              {row.product_id
                                || 'No identificado'}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#b8cddd',
                                fontWeight: 700,
                              }}
                            >
                              {strategyLabel(
                                row,
                              )}
                            </Typography>
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#aac0d3',
                                fontWeight: 750,
                              }}
                            >
                              {formatPrice(
                                row.cost_used,
                              )}
                            </Typography>
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            <Typography
                              variant="body1"
                              sx={{
                                color: '#f8fafc',
                                fontWeight: 900,
                              }}
                            >
                              {formatPrice(
                                row.suggested_price,
                              )}
                            </Typography>
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#a7f5e5',
                                fontWeight: 800,
                              }}
                            >
                              {formatPrice(
                                row.suggested_margin_abs,
                              )}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              label={
                                row.success
                                  ? 'Registrado'
                                  : 'Sin resultado'
                              }
                              color={
                                row.success
                                  ? 'success'
                                  : 'warning'
                              }
                              sx={{
                                height: 22,
                                fontSize: 11,
                                fontWeight: 750,
                              }}
                            />
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            <Button
                              size="small"
                              aria-label="Abrir evaluación"
                              endIcon={
                                <ArrowOutwardRoundedIcon
                                  fontSize="small"
                                />
                              }
                              disabled={
                                !row.optimization_id
                              }
                              // PRC-032: enlace real (se puede abrir en otra pestaña).
                              component={RouterLink}
                              to={evaluationHref(row)}
                              sx={{
                                minWidth: 0,
                                px: 0.5,
                                color: '#7ddfff',
                                fontWeight: 800,
                                textTransform:
                                  'none',
                              }}
                            >
                              Ver
                            </Button>
                          </TableCell>
                        </TableRow>
                      ),
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Divider
              sx={{
                mt: 'auto',
                borderColor:
                  'rgba(148,163,184,0.12)',
              }}
            />

            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: '#70879a',
                px: 2.5,
                py: 1.15,
              }}
            >
              * Importes sin símbolo monetario
              cuando la moneda contractual no
              consta en el registro histórico.
            </Typography>
          </Paper>

          <Box
            sx={{
              display: 'grid',
              gridTemplateRows: {
                xs: 'auto auto',
                xl:
                  'repeat(2, minmax(0, 1fr))',
              },
              gap: 2,
              height: '100%',
              minHeight: 0,
            }}
          >
            <Paper
              component="button"
              type="button"
              elevation={0}
              onClick={() =>
                navigate(
                  withReturnTo('/optimization/history', currentOrigin(location)),
                )
              }
              sx={{
                ...surface,
                p: 2.1,
                width: '100%',
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'left',
                color: 'inherit',
                font: 'inherit',
                cursor: 'pointer',
                transition:
                  'border-color 160ms ease, background-color 160ms ease, transform 160ms ease',
                '&:hover': {
                  bgcolor:
                    'rgba(18,38,58,0.92)',
                  borderColor:
                    'rgba(56,189,248,0.38)',
                  transform:
                    'translateY(-1px)',
                },
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={1.25}
              >
                <Box>
                  <Typography
                    variant="overline"
                    sx={{
                      color: '#38bdf8',
                      fontWeight: 850,
                      letterSpacing: 0.9,
                    }}
                  >
                    Actividad
                  </Typography>

                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 900,
                      mt: 0.15,
                    }}
                  >
                    Historial de optimización
                  </Typography>
                </Box>

                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent:
                      'center',
                    color: '#7dd3fc',
                    bgcolor:
                      'rgba(56,189,248,0.07)',
                    border:
                      '1px solid rgba(56,189,248,0.16)',
                    flexShrink: 0,
                  }}
                >
                  <QueryStatsRoundedIcon
                    fontSize="small"
                  />
                </Box>
              </Stack>

              <Box
                sx={{
                  mt: 1.45,
                  display: 'grid',
                  gridTemplateColumns:
                    'repeat(2, minmax(0, 1fr))',
                  gap: 1.1,
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#7890a5',
                    }}
                  >
                    Última evaluación
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 900,
                      mt: 0.1,
                    }}
                  >
                    {formatDate(
                      latestDate,
                    )}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#7890a5',
                    }}
                  >
                    Registros recuperados
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 900,
                      mt: 0.1,
                    }}
                  >
                    {history.length}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#7890a5',
                    }}
                  >
                    Clientes distintos
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: '#d7ecff',
                      fontWeight: 900,
                      mt: 0.1,
                    }}
                  >
                    {distinctCustomers}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#7890a5',
                    }}
                  >
                    Productos distintos
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: '#d7ecff',
                      fontWeight: 900,
                      mt: 0.1,
                    }}
                  >
                    {distinctProducts}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  mt: 1.15,
                  px: 1.1,
                  py: 0.85,
                  borderRadius: 1.5,
                  bgcolor:
                    'rgba(52,211,153,0.06)',
                  border:
                    '1px solid rgba(52,211,153,0.14)',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#8ea5b9',
                  }}
                >
                  Cálculos registrados
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: '#a7f5e5',
                    fontWeight: 900,
                    mt: 0.1,
                  }}
                >
                  {successfulAvailable}
                  {' '}
                  de
                  {' '}
                  {history.length}
                  {' '}
                  registros recuperados
                </Typography>
              </Box>

              <Box sx={{ flex: 1 }} />

              <Stack
                direction="row"
                alignItems="center"
                spacing={0.6}
                sx={{
                  color: '#7dd3fc',
                  mt: 1.2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 850,
                  }}
                >
                  Abrir historial completo
                </Typography>

                <ArrowForwardRoundedIcon
                  fontSize="small"
                />
              </Stack>

              <Typography
                variant="caption"
                sx={{
                  color: '#60788d',
                  mt: 0.65,
                }}
              >
                Resumen de hasta 50 registros
                recientes; no equivale al
                histórico total.
              </Typography>
            </Paper>

            <Paper
              component="button"
              type="button"
              elevation={0}
              onClick={() =>
                navigate(
                  withReturnTo('/batch-optimization', currentOrigin(location)),
                )
              }
              sx={{
                ...surface,
                p: 2.1,
                width: '100%',
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'left',
                color: 'inherit',
                font: 'inherit',
                cursor: 'pointer',
                transition:
                  'border-color 160ms ease, background-color 160ms ease, transform 160ms ease',
                '&:hover': {
                  bgcolor:
                    'rgba(18,38,58,0.92)',
                  borderColor:
                    'rgba(56,189,248,0.38)',
                  transform:
                    'translateY(-1px)',
                },
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={1.25}
              >
                <Box>
                  <Typography
                    variant="overline"
                    sx={{
                      color: '#38bdf8',
                      fontWeight: 850,
                      letterSpacing: 0.9,
                    }}
                  >
                    Escala
                  </Typography>

                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 900,
                      mt: 0.15,
                    }}
                  >
                    Optimización por lotes
                  </Typography>
                </Box>

                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent:
                      'center',
                    color: '#7dd3fc',
                    bgcolor:
                      'rgba(56,189,248,0.07)',
                    border:
                      '1px solid rgba(56,189,248,0.16)',
                    flexShrink: 0,
                  }}
                >
                  <ViewListRoundedIcon
                    fontSize="small"
                  />
                </Box>
              </Stack>

              {batchJobsQuery.isLoading ? (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mt: 2 }}
                >
                  <CircularProgress
                    size={16}
                  />

                  <Typography
                    variant="body2"
                    sx={{
                      color: '#94a3b8',
                    }}
                  >
                    Consultando ejecuciones…
                  </Typography>
                </Stack>
              ) : batchJobsQuery.isError ? (
                <Alert
                  severity="warning"
                  sx={{
                    mt: 1.4,
                    py: 0.25,
                  }}
                >
                  No se pudo consultar la
                  actividad por lotes.
                </Alert>
              ) : latestBatchJob ? (
                <>
                  <Box
                    sx={{
                      mt: 1.45,
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(2, minmax(0, 1fr))',
                      gap: 1.1,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#7890a5',
                        }}
                      >
                        Último trabajo
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{
                          color: '#f8fafc',
                          fontWeight: 900,
                          mt: 0.1,
                        }}
                      >
                        {formatDate(
                          latestBatchJob.created_at,
                        )}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#7890a5',
                        }}
                      >
                        Estado
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{
                          color:
                            latestBatchJob.status
                              === 'COMPLETED'
                              ? '#a7f5e5'
                              : '#f8fafc',
                          fontWeight: 900,
                          mt: 0.1,
                        }}
                      >
                        {batchStatusLabel(
                          latestBatchJob.status,
                        )}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#7890a5',
                        }}
                      >
                        Filas procesadas
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{
                          color: '#d7ecff',
                          fontWeight: 900,
                          mt: 0.1,
                        }}
                      >
                        {latestBatchProgress}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#7890a5',
                        }}
                      >
                        Trabajos activos
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{
                          color:
                            activeBatchJobs > 0
                              ? '#fde68a'
                              : '#d7ecff',
                          fontWeight: 900,
                          mt: 0.1,
                        }}
                      >
                        {activeBatchJobs}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography
                    variant="caption"
                    sx={{
                      color: '#60788d',
                      mt: 1.15,
                    }}
                  >
                    {batchJobs.length}
                    {' '}
                    trabajos recientes
                    recuperados.
                  </Typography>
                </>
              ) : (
                <Box sx={{ mt: 1.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#d7ecff',
                      fontWeight: 800,
                    }}
                  >
                    Sin ejecuciones recientes
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: '#7890a5',
                      mt: 0.4,
                    }}
                  >
                    Inicia un lote cuando
                    necesites evaluar múltiples
                    casos a la vez.
                  </Typography>
                </Box>
              )}

              <Box sx={{ flex: 1 }} />

              <Stack
                direction="row"
                alignItems="center"
                spacing={0.6}
                sx={{
                  color: '#7dd3fc',
                  mt: 1.2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 850,
                  }}
                >
                  Abrir optimización por lotes
                </Typography>

                <ArrowForwardRoundedIcon
                  fontSize="small"
                />
              </Stack>
            </Paper>
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};


export default OptimizationHubPage;
